import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Building2, 
  Send,
  CheckCircle,
  AlertCircle,
  Loader2,
  Navigation,
  Facebook,
  ExternalLink
} from 'lucide-react';

// Error boundary to suppress cross-origin iframe errors (Google Maps)
class MapErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch() {}
  render() {
    if (this.state.hasError) return <div className="w-full h-[400px] bg-gray-800 flex items-center justify-center text-gray-500">Kartet kunne ikke lastes</div>;
    return this.props.children;
  }
}
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import Layout from '../components/Layout';
import { useSchemaOrg } from '../hooks/useSchemaOrg';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [infoLoading, setInfoLoading] = useState(true);

  // Hent bedriftsinformasjon fra API
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await axios.get(`${API}/company-info`);
        setCompanyInfo(response.data);
      } catch (error) {
        console.error('Error fetching company info:', error);
      } finally {
        setInfoLoading(false);
      }
    };
    fetchCompanyInfo();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSent(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      setError('Kunne ikke sende meldingen. Vennligst prøv igjen eller ring oss direkte.');
    } finally {
      setSending(false);
    }
  };

  // Hjelpefunksjoner for å hente data fra companyInfo med fallbacks
  // VIKTIG: Fallback-koordinatene er for Sand-Tec AS, Industrivegen 4, 2120 Sagstua
  const getContact = () => companyInfo?.contact || {};
  const getAddress = () => companyInfo?.address || {};
  const getCoordinates = () => {
    const coords = companyInfo?.coordinates;
    if (!coords || !coords.lat || !coords.lng) {
      return { lat: 60.4091759, lng: 11.5646682 }; // Sand-Tec AS korrekte koordinater
    }
    return coords;
  };
  const getMapSettings = () => companyInfo?.map_settings || { zoom: 15, map_type: 'roadmap' };
  const getOpeningHours = () => companyInfo?.opening_hours || {};
  const getSocialMedia = () => companyInfo?.social_media || { facebook: 'https://www.facebook.com/sandtecas' };
  const getSpecialHours = () => companyInfo?.special_hours || { is_active: false };

  // Formater åpningstider for visning
  const formatOpeningHours = () => {
    const hours = getOpeningHours();
    const monday = hours.monday;
    const friday = hours.friday;
    const saturday = hours.saturday;
    const sunday = hours.sunday;
    
    return {
      weekdays: monday?.is_open ? `${monday.open} - ${monday.close}` : 'Stengt',
      friday: friday?.is_open ? `${friday.open} - ${friday.close}` : 'Stengt',
      saturday: saturday?.is_open ? `${saturday.open} - ${saturday.close}` : 'Stengt',
      sunday: sunday?.is_open ? `${sunday.open} - ${sunday.close}` : 'Stengt'
    };
  };

  // Bygg Google Maps embed URL
  const buildMapUrl = () => {
    const mapSettings = getMapSettings();
    const coords = getCoordinates();
    
    // Hvis en egendefinert embed-URL er satt, bruk den
    if (mapSettings.custom_embed_url) {
      return mapSettings.custom_embed_url;
    }
    
    const zoom = mapSettings.zoom || 15;
    const lat = coords.lat;
    const lng = coords.lng;
    
    // Bruk Google Maps embed som fungerer (2024+ format)
    // Format: https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d{distance}!2d{lng}!3d{lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sno!2sno!4v{timestamp}
    const distance = Math.round(50000 / Math.pow(2, zoom - 10)); // Approximate distance based on zoom
    const timestamp = Date.now();
    
    return `https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d${distance}!2d${lng}!3d${lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sno!2sno!4v${timestamp}`;
  };

  // Bygg veibeskrivelse URL
  const buildDirectionsUrl = () => {
    const address = getAddress();
    const addressQuery = `${address.street}, ${address.postal_code} ${address.city}, Norway`;
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addressQuery)}`;
  };

  const contact = getContact();
  const address = getAddress();
  const openingHours = formatOpeningHours();
  const specialHours = getSpecialHours();
  const { name: companyName, publisher, postalAddress, geo, logoUrl, phone, email, SITE_URL } = useSchemaOrg();
  const socialMedia = getSocialMedia();

  // Structured Data for Local Business - dynamic from company_info
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AutoRepair",
    "@id": `${SITE_URL}/#organization`,
    "name": companyName,
    "description": `Profesjonelt verksted for EU-kontroll, service og reparasjon i ${postalAddress.addressLocality || 'Nord-Odal'}.`,
    "url": SITE_URL,
    ...(logoUrl ? { "image": logoUrl } : {}),
    "telephone": phone,
    "email": email,
    "legalName": companyName,
    "address": postalAddress,
    ...(geo ? { "geo": geo } : {}),
    "priceRange": "$$",
    "paymentAccepted": ["Cash", "Credit Card", "Invoice"],
    "currenciesAccepted": "NOK",
  };

  return (
    <Layout>
      <Helmet>
        <title>Kontakt oss | {companyInfo?.company_name || ''}</title>
        <meta name="description" content={`Kontakt ${companyInfo?.company_name || 'oss'} for EU-kontroll, service og reparasjon.`} />
        <meta name="keywords" content="kontakt, eu-kontroll, verksted, tungbil service" />
        
        {/* Open Graph */}
        <meta property="og:title" content={`Kontakt ${companyInfo?.company_name || 'oss'}`} />
        <meta property="og:description" content="EU-kontroll og verkstedtjenester." />
        <meta property="og:type" content="website" />
        
        {/* Geo tags for local SEO */}
        <meta name="geo.region" content="NO-34" />
        <meta name="geo.placename" content="Sagstua, Nord-Odal" />
        <meta name="geo.position" content="60.4091759;11.5646682" />
        <meta name="ICBM" content="60.4091759, 11.5646682" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Noscript fallback for AI agents, Bingbot and crawlers without JS */}
      <noscript>
        <div style={{maxWidth: '900px', margin: '0 auto', padding: '30px 20px', fontFamily: 'system-ui, sans-serif', backgroundColor: '#fff', color: '#1a1a1a'}}>
          <header style={{borderBottom: '3px solid #dc2626', paddingBottom: '20px', marginBottom: '30px'}}>
            <h1 style={{fontSize: '2em', color: '#dc2626', margin: '0 0 8px'}}>Kontakt Sand-tec AS</h1>
            <p style={{fontSize: '1.1em', color: '#555', margin: 0}}>EU-kontroll og verkstedtjenester i Sagstua, Nord-Odal. Vi er her for deg — kontakt oss for timebestilling eller spørsmål.</p>
          </header>

          <section style={{marginBottom: '30px', display: 'flex', flexWrap: 'wrap', gap: '20px'}}>
            <div style={{flex: '1 1 300px'}}>
              <h2 style={{fontSize: '1.3em', color: '#1f2937', marginBottom: '15px'}}>Kontaktinformasjon</h2>
              <table style={{width: '100%', borderCollapse: 'collapse'}}>
                <tbody>
                  <tr style={{borderBottom: '1px solid #e5e7eb'}}><td style={{padding: '10px', fontWeight: 'bold', color: '#374151'}}>Adresse</td><td style={{padding: '10px'}}>Industrivegen 4, 2120 Sagstua, Norge</td></tr>
                  <tr style={{borderBottom: '1px solid #e5e7eb'}}><td style={{padding: '10px', fontWeight: 'bold', color: '#374151'}}>Telefon</td><td style={{padding: '10px'}}><a href="tel:+4762974050" style={{color: '#dc2626'}}>+47 62 97 40 50</a></td></tr>
                  <tr style={{borderBottom: '1px solid #e5e7eb'}}><td style={{padding: '10px', fontWeight: 'bold', color: '#374151'}}>E-post</td><td style={{padding: '10px'}}><a href="mailto:post@sand-tec.no" style={{color: '#dc2626'}}>post@sand-tec.no</a></td></tr>
                  <tr style={{borderBottom: '1px solid #e5e7eb'}}><td style={{padding: '10px', fontWeight: 'bold', color: '#374151'}}>Org.nr.</td><td style={{padding: '10px'}}>924 794 564</td></tr>
                </tbody>
              </table>
            </div>
            <div style={{flex: '1 1 300px'}}>
              <h2 style={{fontSize: '1.3em', color: '#1f2937', marginBottom: '15px'}}>Åpningstider</h2>
              <table style={{width: '100%', borderCollapse: 'collapse'}}>
                <tbody>
                  <tr style={{borderBottom: '1px solid #e5e7eb'}}><td style={{padding: '8px'}}>Mandag – Torsdag</td><td style={{padding: '8px', fontWeight: 'bold'}}>07:00 – 16:00</td></tr>
                  <tr style={{borderBottom: '1px solid #e5e7eb'}}><td style={{padding: '8px'}}>Fredag</td><td style={{padding: '8px', fontWeight: 'bold'}}>07:00 – 15:30</td></tr>
                  <tr style={{borderBottom: '1px solid #e5e7eb'}}><td style={{padding: '8px'}}>Lørdag – Søndag</td><td style={{padding: '8px', fontWeight: 'bold', color: '#dc2626'}}>Stengt</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section style={{marginBottom: '30px'}}>
            <h2 style={{fontSize: '1.3em', color: '#1f2937', marginBottom: '10px'}}>Beliggenhet</h2>
            <p style={{lineHeight: '1.7', color: '#374151'}}>Sand-tec AS ligger i Industrivegen 4, 2120 Sagstua i Nord-Odal kommune, Innlandet fylke. Verkstedet er lett tilgjengelig fra E2/RV2 og har god plass for store kjøretøy inkludert semitrailere og vogntog. GPS-koordinater: 60.4091759°N, 11.5646682°Ø. <a href="https://www.google.com/maps/place/Sand-Tec+AS/@60.4091759,11.5646682" style={{color: '#dc2626'}}>Se på Google Maps</a>.</p>
          </section>

          <nav style={{marginBottom: '30px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px'}}>
            <h3 style={{fontSize: '1.1em', color: '#374151', marginBottom: '10px'}}>Navigasjon</h3>
            <ul style={{listStyle: 'none', padding: 0, display: 'flex', flexWrap: 'wrap', gap: '10px'}}>
              <li><a href="/" style={{color: '#dc2626'}}>Forside</a></li>
              <li><a href="/tjenester" style={{color: '#dc2626'}}>Tjenester</a></li>
              <li><a href="/kampanjer" style={{color: '#dc2626'}}>Kampanjer</a></li>
              <li><a href="/aktuelt" style={{color: '#dc2626'}}>Aktuelt</a></li>
              <li><a href="/referanser" style={{color: '#dc2626'}}>Referanser</a></li>
              <li><a href="/anmeldelser" style={{color: '#dc2626'}}>Anmeldelser</a></li>
            </ul>
          </nav>

          <footer style={{borderTop: '1px solid #e5e7eb', paddingTop: '15px', fontSize: '0.9em', color: '#6b7280'}}>
            <p>For maskinlesbar bedriftsinformasjon, se <a href="/api/llms.txt" style={{color: '#dc2626'}}>llms.txt</a> | <a href="/api/llms-full.txt" style={{color: '#dc2626'}}>llms-full.txt</a></p>
          </footer>
        </div>
      </noscript>

      <div className="min-h-screen bg-black">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-gray-900 via-black to-red-900/20 pt-32 pb-16">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(239,68,68,0.1),transparent_50%)]"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <Badge className="bg-red-600/20 text-red-400 border-red-600/50 mb-4">
              Kontakt
            </Badge>
            <h1 className="text-4xl md:text-6xl font-black mb-6">
              <span className="text-white">Ta kontakt med </span>
              <span className="bg-gradient-to-r from-red-500 to-yellow-500 bg-clip-text text-transparent">Sand-tec AS</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl">
              Vi er her for å hjelpe deg med EU-kontroll, service og reparasjon av tungbil, tilhenger og personbil.
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-16">
          
          {/* Special Hours Notice */}
          {specialHours.is_active && (
            <div className="max-w-6xl mx-auto mb-8">
              <div className="bg-gradient-to-r from-yellow-600/20 to-orange-600/20 border border-yellow-500/50 rounded-xl p-6 backdrop-blur-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-6 h-6 text-black" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-yellow-400 mb-2">{specialHours.title || 'Avvikende åpningstider'}</h3>
                    <p className="text-gray-300 mb-3">{specialHours.message}</p>
                    {specialHours.hours && specialHours.hours.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
                        {specialHours.hours.map((day, index) => (
                          <div key={index} className="bg-black/30 rounded-lg p-2 text-sm">
                            <span className="text-gray-400">{day.label || day.date}:</span>{' '}
                            <span className={day.is_open ? 'text-green-400' : 'text-red-400'}>
                              {day.is_open ? `${day.open} - ${day.close}` : 'Stengt'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            
            {/* Contact Information */}
            <div className="space-y-8">
              
              {/* NAP Card - Critical for Local SEO */}
              <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-2xl text-white flex items-center gap-2">
                    <Building2 className="w-6 h-6 text-red-500" />
                    Firmainformasjon
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Company Name */}
                  <div>
                    <h2 className="text-2xl font-bold text-white" itemProp="name">
                      {companyInfo?.company_name || 'Sand-tec AS'}
                    </h2>
                    <p className="text-gray-400">{companyInfo?.tagline || 'Godkjent kontrollverksted for tungbil og tilhenger'}</p>
                  </div>

                  {/* Address */}
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-red-600/20 rounded-lg">
                      <MapPin className="w-6 h-6 text-red-500" />
                    </div>
                    <div itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                      <p className="font-semibold text-white">Besøksadresse</p>
                      <p className="text-gray-300" itemProp="streetAddress">{address.street}</p>
                      <p className="text-gray-300">
                        <span itemProp="postalCode">{address.postal_code}</span>{' '}
                        <span itemProp="addressLocality">{address.city}</span>
                      </p>
                      <p className="text-gray-400" itemProp="addressCountry">{address.country || 'Norge'}</p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-green-600/20 rounded-lg">
                      <Phone className="w-6 h-6 text-green-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">Telefon</p>
                      <a 
                        href={`tel:${contact.phone}`} 
                        className="text-2xl font-bold text-green-400 hover:text-green-300 transition-colors"
                        itemProp="telephone"
                      >
                        {contact.phone_display || contact.phone}
                      </a>
                      <p className="text-gray-400 text-sm mt-1">Ring oss i åpningstiden</p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-blue-600/20 rounded-lg">
                      <Mail className="w-6 h-6 text-blue-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">E-post</p>
                      <a 
                        href={`mailto:${contact.email}`} 
                        className="text-lg text-blue-400 hover:text-blue-300 transition-colors"
                        itemProp="email"
                      >
                        {contact.email}
                      </a>
                      <p className="text-gray-400 text-sm mt-1">Vi svarer innen 24 timer</p>
                    </div>
                  </div>

                  {/* Org Number */}
                  <div className="pt-4 border-t border-gray-700">
                    <p className="text-gray-400">
                      <span className="font-semibold text-gray-300">Org.nr:</span>{' '}
                      <span itemProp="vatID">{companyInfo?.org_number || '923 456 789'} MVA</span>
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Opening Hours */}
              <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-xl text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-yellow-500" />
                    Åpningstider
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-700">
                      <span className="text-gray-300">Mandag - Torsdag</span>
                      <span className={`font-semibold ${openingHours.weekdays === 'Stengt' ? 'text-red-400' : 'text-white'}`}>
                        {openingHours.weekdays}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-700">
                      <span className="text-gray-300">Fredag</span>
                      <span className={`font-semibold ${openingHours.friday === 'Stengt' ? 'text-red-400' : 'text-white'}`}>
                        {openingHours.friday}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-700">
                      <span className="text-gray-300">Lørdag</span>
                      <span className={`font-semibold ${openingHours.saturday === 'Stengt' ? 'text-red-400' : 'text-white'}`}>
                        {openingHours.saturday}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-300">Søndag</span>
                      <span className={`font-semibold ${openingHours.sunday === 'Stengt' ? 'text-red-400' : 'text-white'}`}>
                        {openingHours.sunday}
                      </span>
                    </div>
                  </div>
                  {companyInfo?.opening_hours_note && (
                    <p className="text-gray-400 text-sm mt-4">
                      * {companyInfo.opening_hours_note}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-4">
                <a
                  href={`tel:${contact.phone}`}
                  className="flex items-center justify-center gap-2 p-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-all"
                  data-testid="contact-call-btn"
                >
                  <Phone className="w-5 h-5" />
                  Ring nå
                </a>
                <a
                  href={buildDirectionsUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 p-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all"
                  data-testid="contact-directions-btn"
                >
                  <Navigation className="w-5 h-5" />
                  Veibeskrivelse
                </a>
              </div>

              {/* Social Media */}
              <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
                <CardContent className="pt-6">
                  <p className="text-gray-300 mb-4">Følg oss på sosiale medier:</p>
                  <div className="flex flex-wrap gap-4">
                    {socialMedia.facebook && (
                      <a
                        href={socialMedia.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded-lg transition-all"
                      >
                        <Facebook className="w-5 h-5" />
                        Facebook
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700 sticky top-24">
                <CardHeader>
                  <CardTitle className="text-2xl text-white flex items-center gap-2">
                    <Send className="w-6 h-6 text-red-500" />
                    Send oss en melding
                  </CardTitle>
                  <p className="text-gray-400">
                    Fyll ut skjemaet så tar vi kontakt med deg så snart som mulig.
                  </p>
                </CardHeader>
                <CardContent>
                  {sent ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                      <h3 className="text-2xl font-bold text-white mb-2">Takk for din henvendelse!</h3>
                      <p className="text-gray-400 mb-6">Vi vil ta kontakt med deg så snart som mulig.</p>
                      <Button 
                        onClick={() => setSent(false)}
                        variant="outline"
                        className="border-gray-600 text-gray-300"
                      >
                        Send ny melding
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {error && (
                        <div className="flex items-center gap-2 p-4 bg-red-900/30 border border-red-800 rounded-lg text-red-300">
                          <AlertCircle className="w-5 h-5 flex-shrink-0" />
                          <p>{error}</p>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name" className="text-gray-300">Navn *</Label>
                          <Input
                            id="name"
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            className="bg-gray-800 border-gray-600 text-white"
                            placeholder="Ditt navn"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-gray-300">Telefon</Label>
                          <Input
                            id="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({...formData, phone: e.target.value})}
                            className="bg-gray-800 border-gray-600 text-white"
                            placeholder="+47 XXX XX XXX"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-gray-300">E-post *</Label>
                        <Input
                          id="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="bg-gray-800 border-gray-600 text-white"
                          placeholder="din@epost.no"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="subject" className="text-gray-300">Emne *</Label>
                        <Input
                          id="subject"
                          type="text"
                          required
                          value={formData.subject}
                          onChange={(e) => setFormData({...formData, subject: e.target.value})}
                          className="bg-gray-800 border-gray-600 text-white"
                          placeholder="Hva gjelder henvendelsen?"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message" className="text-gray-300">Melding *</Label>
                        <Textarea
                          id="message"
                          required
                          rows={5}
                          value={formData.message}
                          onChange={(e) => setFormData({...formData, message: e.target.value})}
                          className="bg-gray-800 border-gray-600 text-white"
                          placeholder="Beskriv din henvendelse..."
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={sending}
                        className="w-full bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white font-semibold py-6"
                      >
                        {sending ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Sender...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5 mr-2" />
                            Send melding
                          </>
                        )}
                      </Button>

                      <p className="text-gray-500 text-sm text-center">
                        Ved å sende dette skjemaet godtar du vår{' '}
                        <a href="/personvern" className="text-red-400 hover:underline">
                          personvernerklæring
                        </a>.
                      </p>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Map Section */}
          <div className="mt-16" data-testid="contact-map-section">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <MapPin className="w-6 h-6 text-red-500" />
              Finn oss
            </h2>
            <div className="rounded-2xl overflow-hidden border border-gray-700">
              {infoLoading ? (
                <div className="w-full h-[400px] bg-gray-800 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
                </div>
              ) : (
              <MapErrorBoundary>
                <iframe
                  src={buildMapUrl()}
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`${companyInfo?.company_name || 'Sand-tec AS'} lokasjon`}
                  className="grayscale hover:grayscale-0 transition-all duration-500"
                ></iframe>
              </MapErrorBoundary>
              )}
            </div>
            <p className="text-gray-400 text-sm mt-4">
              Vi holder til i {address.street} i {address.city}{address.municipality ? `, ${address.municipality}` : ''}. Verkstedet ligger lett tilgjengelig med god plass til store kjøretøy.
            </p>
          </div>

          {/* FAQ Section for AI visibility */}
          <section className="mt-16">
            <h2 className="text-2xl font-bold text-white mb-6">Ofte stilte spørsmål om kontakt</h2>
            <div className="space-y-4">
              <details className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 group">
                <summary className="font-semibold text-white cursor-pointer list-none flex justify-between items-center">
                  Hvordan bestiller jeg time for EU-kontroll?
                  <span className="text-red-500 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="text-gray-300 mt-3">
                  Du kan bestille time ved å ringe oss i åpningstiden, sende e-post,
                  eller bruke kontaktskjemaet på denne siden. Vi tar kontakt for å avtale tid som passer for deg.
                </p>
              </details>
              
              <details className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 group">
                <summary className="font-semibold text-white cursor-pointer list-none flex justify-between items-center">
                  Hvor finner vi dere?
                  <span className="text-red-500 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="text-gray-300 mt-3">
                  Se adressen øverst på denne siden. 
                  Verkstedet ligger sentralt med enkel adkomst for store kjøretøy og tilhengere.
                </p>
              </details>
              
              <details className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 group">
                <summary className="font-semibold text-white cursor-pointer list-none flex justify-between items-center">
                  Hva er åpningstidene?
                  <span className="text-red-500 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="text-gray-300 mt-3">
                  Se åpningstidene som vises øverst på denne siden.
                  Vi holder stengt i helger og helligdager.
                </p>
              </details>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default ContactPage;
